import {
  anchorDisplayDate,
  formatDisplayDateAtPrecision,
  resolveLocalized,
  toCanonicalDay,
  toDisplayDate,
  type Calendar,
  type CanonicalDay,
  type DisplayDate,
  type Era,
} from '@dl/calendar';
import { type Precision } from '@dl/shared';
import { button, div, input, onMount, span, text, type Mountable } from '@llui/dom';

import { chevronDownIcon } from './icon.js';

/**
 * `<CalendarDatePicker>` — a date picker that renders ANY fictional
 * calendar from a real `@dl/calendar` `Calendar` definition.
 *
 * Faithful to the design mock `mocks/Calendar Date Picker (standalone)` —
 * the 7-zone adaptive shell that consumes a definition and renders
 * whatever it is handed:
 *
 *  1. **Step nav** — prev / next move one *block* (a month, or an
 *     intercalary festival band), stepping the year boundary automatically.
 *  2. **Month control** — opens the month list (festivals included,
 *     italicised). Label is the localized month name.
 *  3. **Year + era control** — opens a year stepper + an era segmented
 *     control. Crossing the calendar anchor flips the era (forward ↔
 *     backward numbering), skipping a non-existent year zero.
 *  4. **Weekday header** — one column per `cycles.week.labels` entry.
 *     The whole row is OMITTED when the calendar declares no week — the
 *     grid then degrades to a numbered **day list** (zone 5b).
 *  5. **Day grid** — column count = week length; leading blanks honour
 *     the month's start phase; selected = filled, today = ringed.
 *  5b. **Day list** — the no-week fallback: a single scrolling column.
 *  6. **Time row** — only present when `subDayPeriods` is passed (the
 *     core `Calendar` schema has no sub-day field yet — ADR-0006 §1 keeps
 *     sub-day resolution in a separate `seconds_into_day` axis — so the
 *     host opts in by handing the named periods to the widget).
 *  7. **Footer** — the resolved value (serif) + a canonical-day sub-line,
 *     a "Now" jump, and Clear.
 *
 * Adaptive behaviour, all driven by the definition (never hard-coded):
 *
 *  - **week / no-week** — `cycles.week` present → column grid; absent →
 *    day list.
 *  - **intercalary / festival days** — a month flagged
 *    `intercalary: true` renders as a band BETWEEN the surrounding
 *    months, never as cells inside a grid (Harptos's Midwinter /
 *    Shieldmeet, Shire's Midyear's Day).
 *  - **leap days** — a month whose `leap_length > length` only exposes
 *    its extra day(s) in leap years; the day-count engine
 *    (`toCanonicalDay`) is the single source of truth for which days
 *    exist, so a leap-only festival day cannot be selected in a common
 *    year.
 *  - **eras, forward AND backward** — each `Era.direction` decides which
 *    way the year stepper counts; backward (BCE-style) eras count *down*
 *    toward the anchor. Deep-history negative canonical days are
 *    first-class.
 *  - **sub-day periods** — rendered only when `subDayPeriods` is given.
 *
 * ## Date math chokepoint
 *
 * The widget NEVER reimplements the canonical-day bijection. Every
 * `CanonicalDay ↔ DisplayDate` move goes through `toCanonicalDay` /
 * `toDisplayDate`, and every month-length / leap question is answered by
 * probing `toCanonicalDay` (an accepted conversion ⇒ the day exists).
 * This keeps `@dl/calendar` the single axis-of-truth (ADR-0006 §1).
 *
 * ## Interactivity model — STATELESS atom (state lives in the host)
 *
 * `@dl/ui` atoms are stateless and rendered DECLARATIVELY (no
 * `document.createElement`, no imperative body-build in `onMount`). The
 * picker is a pure function of its props: it takes the *view cursor*
 * (`cursor` — which month/year/era is on screen), the committed `value`,
 * and the open `overlay` as props, and emits the user's intent through
 * callbacks (`onNavigate`, `onSelect`, `onClear`, `onPeriod`,
 * `onOverlay`). The HOST owns those slices in its Model and re-renders on
 * change — wrapping the picker in a keyed `each` so it remounts cleanly
 * (the same construction-time pattern `tabs` / `validityEditor` use).
 *
 * When `cursor` is omitted the picker derives a sensible starting view
 * from `value` (or the calendar anchor), so a fixed/static specimen — the
 * gallery, a read-only preview — renders a populated grid with no host
 * wiring at all. `onMount` is used ONLY for a side-effect: focusing the
 * roving grid cell, never to build content.
 *
 * ## A11y
 *
 * Real `<button>`s throughout. The grid is a `role="grid"` with
 * `aria-label`; arrow keys move by day / week, `Home`/`End` jump to the
 * row ends, `PageUp`/`PageDown` step the block, `Enter`/`Space` select.
 * The no-week list is `role="listbox"` with `↑`/`↓` navigation. Selection
 * pairs the accent fill with `aria-pressed` / `aria-selected` so colour is
 * never the only signal. All copy flows through the `messages` catalog;
 * month / weekday / era labels come from the definition (localized).
 */

/** A sub-day period the calendar names (watch / bell / hour). Optional —
 * the core `Calendar` schema declares no sub-day axis, so the host passes
 * these in when the world keeps sub-day time. */
export interface SubDayPeriod {
  /** Stable identifier. */
  name: string;
  /** Localized display label; falls back to `name`. */
  label?: string;
  /** Optional exact-time hint shown beneath the label (e.g. `"12:00"`). */
  time?: string;
}

export type CalendarDatePickerMode = 'popover' | 'inline';

/**
 * How coarsely the picker selects (ADR-0005 §5). `day` (default) is the full
 * day grid; coarser levels present only down to their unit and commit the
 * START of it (year → first day of the year, month/season → first day of the
 * month). The committed value is always a canonical day; the granularity only
 * changes WHAT the user picks. (No season *cycle* exists yet — `season` renders
 * the month picker, the closest representable unit.)
 */
export type CalendarGranularity = 'day' | 'season' | 'month' | 'year';

/** Which inline overlay is open (host-owned). */
export type CalendarDatePickerOverlay = 'none' | 'months' | 'era';

/**
 * The view cursor — which (era, year, month-block) is currently displayed.
 * This is NOT the committed selection; it is purely what the grid shows.
 * The host owns it in its Model and updates it from `onNavigate`.
 */
export interface CalendarCursor {
  /** Era id (matches an `Era.id` in the calendar). */
  eraId: string;
  /** Display year within that era. */
  year: number;
  /** 1-based month index into `cycles.year.months`. */
  monthIndex: number;
}

/** Catalog of localizable strings. Hosts override to integrate `t()`. */
export interface CalendarDatePickerMessages {
  prev: string;
  next: string;
  /** ARIA label for the month control. */
  month: string;
  /** ARIA label for the year + era control. */
  yearEra: string;
  /** ARIA label for the day grid / list. */
  grid: string;
  now: string;
  clear: string;
  set: string;
  /** Footer empty-state when nothing is selected. */
  unset: string;
  /** Time-row heading (e.g. "Time" / "Watch"). */
  time: string;
  /** Festival-band caption prefix (e.g. "Intercalary"). */
  intercalary: string;
  /** Sub-line affix: `"canonical day {day}"`. */
  canonicalDay: string;
  /** No-week day-list row label; `{day}` is interpolated (e.g. "Day {day}"). */
  dayN: string;
  /** Hint shown under a backward (BCE-style) era stepper; `{era}` is interpolated. Empty → no hint. */
  backwardEraHint: string;
  /** Commit label for the year-granularity picker (e.g. "Select year"). */
  selectYear: string;
  /** ARIA label for the granularity segmented control (when `granularities` is given). */
  granularity: string;
  /** Labels for each granularity segment, fine → coarse. `day` = an exact day. */
  granularityOptions: {
    day: string;
    month: string;
    season: string;
    year: string;
  };
}

export const DEFAULT_CALENDAR_DATE_PICKER_MESSAGES: CalendarDatePickerMessages = {
  prev: 'Previous',
  next: 'Next',
  month: 'Month',
  yearEra: 'Year and era',
  grid: 'Days',
  now: 'Now',
  clear: 'Clear',
  set: 'Set',
  unset: 'No date set',
  time: 'Time',
  intercalary: 'Intercalary',
  canonicalDay: 'canonical day',
  dayN: 'Day {day}',
  backwardEraHint: '{era} counts backward toward the anchor; there is no year zero.',
  selectYear: 'Select year',
  granularity: 'Date precision',
  granularityOptions: {
    day: 'Day',
    month: 'Month',
    season: 'Season',
    year: 'Year',
  },
};

export interface CalendarDatePickerOptions {
  /** Active calendar (from `@dl/calendar`'s pack loader / a fixture). */
  calendar: Calendar;
  /**
   * Currently committed canonical day, or `null` for no selection.
   * Accepts a raw axis `number` at the boundary; `onSelect` emits a
   * branded `CanonicalDay`.
   */
  value?: CanonicalDay | number | null;
  /**
   * The view cursor (which era/year/month-block is displayed). HOST-OWNED.
   * Omit to derive the starting view from `value` (or the calendar anchor) —
   * useful for a fixed/static specimen with no navigation wiring.
   */
  cursor?: CalendarCursor;
  /** Which inline overlay is open. HOST-OWNED; defaults to `'none'`. */
  overlay?: CalendarDatePickerOverlay;
  /**
   * Called when the user navigates (prev/next, month pick, year step, era
   * switch, "Now"). The host stores the new cursor in its Model.
   */
  onNavigate?: (cursor: CalendarCursor) => void;
  /** Called when the user opens / closes / switches the inline overlay. */
  onOverlay?: (overlay: CalendarDatePickerOverlay) => void;
  /** Called when the user commits a day. */
  onSelect?: (day: CanonicalDay) => void;
  /** Called when the user clears the selection. */
  onClear?: () => void;
  /** `popover` (default, elevated card) or `inline` (no shadow). */
  mode?: CalendarDatePickerMode;
  /**
   * Sub-day periods to expose as the time row. Omit / empty → no time row
   * (a day-only calendar). The core schema has no sub-day axis, so this is
   * an explicit host opt-in rather than read from the definition.
   */
  subDayPeriods?: readonly SubDayPeriod[];
  /** Index of the active sub-day period (when `subDayPeriods` is given). */
  periodIndex?: number;
  /** Called when the user picks a sub-day period. */
  onPeriod?: (index: number) => void;
  /** "Today" / session-cursor canonical day — ringed in the grid. */
  today?: CanonicalDay | number | null;
  /**
   * Show the footer "Now" jump (to `today`). Default `true`. Set `false` where
   * "now" IS the value being edited (the campaign-date cursor), so the jump
   * would be circular — other placements (as-of scrubber, fact dates) keep it.
   */
  showNow?: boolean;
  /**
   * Show the footer's "canonical day N" sub-line. Default `false` — it's an
   * engineering/debug detail, not GM-facing. Opt in for diagnostics.
   */
  showCanonicalDay?: boolean;
  /**
   * Selection granularity (ADR-0005 §5). `day` (default) = the full day grid;
   * `month`/`season` = a month picker (commits the 1st of the month); `year` =
   * a year stepper + era control (commits the 1st of the year). Drives both
   * what is rendered and what `onSelect` commits. Wire it from a validity
   * bound's precision so a fuzzy bound is picked at its own granularity.
   */
  granularity?: CalendarGranularity;
  /**
   * The selectable granularity levels. When MORE THAN ONE is given, a
   * segmented control (Day · Month · Season · Year, fine → coarse) renders at
   * the top of the card and `onGranularity` fires on a switch — the picker
   * becomes a self-contained fuzzy-date picker that owns its own precision.
   * Omit (or pass a single level) to hide the control and behave as a plain
   * fixed-granularity picker (the historical default).
   */
  granularities?: readonly CalendarGranularity[];
  /** Called when the user switches granularity via the segmented control. */
  onGranularity?: (granularity: CalendarGranularity) => void;
  /**
   * Label for an optional FIRST segment in the granularity control that clears
   * the value (sets it to "none"). When set, the segment renders ahead of the
   * granularity levels and clicking it fires `onGranularityClear`. Use it for a
   * "no date / unbounded / unknown" choice (e.g. an open-ended validity bound).
   */
  granularityClearLabel?: string;
  /** Called when the user picks the clear ("none") segment. */
  onGranularityClear?: () => void;
  /** Display locale for labels. Defaults to `en`. */
  locale?: string;
  /** Optional message-catalog override. */
  messages?: Partial<CalendarDatePickerMessages>;
  /**
   * Optional content rendered as the FIRST row inside the card, above the
   * step-nav head — e.g. a calendar-type switcher. Kept inside the same
   * elevated card (not a detached popover sibling) so it reads as one control.
   */
  header?: Mountable;
  /** Optional `data-testid` prefix to ease E2E. */
  testid?: string;
}

// ---------- block model ----------
//
// A "block" is one navigable unit. A normal month is a `grid` block; a
// month flagged `intercalary: true` is a `festival` block (rendered as a
// band, never as grid cells). Block index == month index (1-based), so
// stepping is just `±1` with year wrap. Whether a festival block's later
// days exist in a given year is resolved by the engine (leap days).

interface BlockBase {
  /** 1-based month index in `cycles.year.months`. */
  month: number;
  label: string;
}
interface GridBlock extends BlockBase {
  type: 'grid';
}
interface FestivalBlock extends BlockBase {
  type: 'festival';
  /** Day count present in the *current* year (leap-aware via the engine). */
  days: number;
}
type Block = GridBlock | FestivalBlock;

function localizedMonth(calendar: Calendar, monthIndex: number, locale: string): string {
  const m = calendar.cycles.year.months[monthIndex - 1];
  if (m === undefined) return '';
  if (m.label !== undefined) {
    const r = resolveLocalized(m.label, locale);
    if (r.length > 0) return r;
  }
  return titleCase(m.name);
}

function titleCase(s: string): string {
  return s
    .split(/[_\s]+/)
    .filter((w) => w.length > 0)
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function localizedWeekday(calendar: Calendar, index: number, locale: string): string {
  const week = calendar.cycles.week;
  if (week === undefined) return '';
  const entry = week.labels[index];
  if (entry === undefined) return '';
  if (typeof entry === 'string') return entry;
  if (entry.label !== undefined) {
    const r = resolveLocalized(entry.label, locale);
    if (r.length > 0) return r;
  }
  return entry.name;
}

function eraById(calendar: Calendar, id: string): Era | undefined {
  return calendar.eras.find((e) => e.id === id);
}

function eraLabel(era: Era, locale: string): string {
  if (era.abbreviation !== undefined) return era.abbreviation;
  if (era.label !== undefined) {
    const r = resolveLocalized(era.label, locale);
    if (r.length > 0) return r;
  }
  return era.name;
}

/**
 * The number of days a (year, monthIndex) actually has — answered by the
 * day-count engine, not by reading `length`/`leap_length` directly, so the
 * leap rule is honoured in one place. Probes downward from `leap_length`.
 */
function daysInMonth(calendar: Calendar, era: string, year: number, monthIndex: number): number {
  const m = calendar.cycles.year.months[monthIndex - 1];
  if (m === undefined) return 0;
  const max = m.leap_length !== undefined ? Math.max(m.length, m.leap_length) : m.length;
  for (let d = max; d >= 1; d--) {
    if (toCanonicalDay(calendar, { era, year, month: monthIndex, day: d }).ok) return d;
  }
  return 0;
}

/**
 * Whether this calendar models SEASONS as a cycle distinct from months. The
 * Phase-1 `Calendar` schema has only a months cycle (+ optional weekdays), so a
 * "season" precision collapses to the month picker — there is nothing extra to
 * select. Until a real season cycle lands (06-timeline.md §4) this is always
 * false, so the granularity menu drops the redundant Season level (the menu
 * reflects only what the definition actually exposes).
 */
function calendarHasSeasons(_calendar: Calendar): boolean {
  return false;
}

/** The data-model precision a picker granularity reads at (for display). `day`
 * is exact (no hint → full date); coarser levels map 1:1. */
function precisionForGranularity(g: CalendarGranularity): Precision | undefined {
  return g === 'day' ? undefined : g;
}

/** Build the block list for a (era, year): one entry per month, in order. */
function blocksFor(calendar: Calendar, era: string, year: number, locale: string): Block[] {
  return calendar.cycles.year.months.map((m, i): Block => {
    const month = i + 1;
    const label = localizedMonth(calendar, month, locale);
    if (m.intercalary === true) {
      return { type: 'festival', month, label, days: daysInMonth(calendar, era, year, month) };
    }
    return { type: 'grid', month, label };
  });
}

/** The weekday index (0-based) of a given display date, via the engine. */
function weekdayIndexOf(calendar: Calendar, date: DisplayDate): number | null {
  const week = calendar.cycles.week;
  if (week === undefined) return null;
  const canonical = toCanonicalDay(calendar, date);
  if (!canonical.ok) return null;
  const back = toDisplayDate(calendar, canonical.value);
  if (!back.ok || back.value.weekday === undefined) return null;
  const idx = week.labels.findIndex((l) => (typeof l === 'string' ? l : l.name) === back.value.weekday);
  return idx >= 0 ? idx : null;
}

/** Neutral starting display date when nothing is selected (the anchor). */
function neutralDate(calendar: Calendar): DisplayDate {
  const a = anchorDisplayDate(calendar);
  if (a.ok) return { era: a.value.era, year: a.value.year, month: a.value.month, day: a.value.day };
  const e0 = calendar.eras[0];
  return { era: e0?.id ?? '', year: e0?.year_one ?? 1, month: 1, day: 1 };
}

/** A coarse "days in a year" used only to over-step the canonical axis when
 * crossing an era boundary; the result is always re-snapped via
 * `toDisplayDate`, so precision here is not load-bearing. */
function approxYearLength(calendar: Calendar): number {
  let total = 0;
  for (const m of calendar.cycles.year.months) total += m.length;
  return Math.max(1, total);
}

// ---------- pure cursor transforms (no internal state) ----------
//
// Each navigation handler computes the NEXT cursor from the CURRENT one and
// hands it to `onNavigate`. The host owns the cursor; nothing mutates here.

/** Step the displayed block by `delta`, wrapping the year boundary. */
function stepBlockCursor(calendar: Calendar, cur: CalendarCursor, delta: number): CalendarCursor {
  let { year, monthIndex } = cur;
  monthIndex += delta;
  const count = calendar.cycles.year.months.length;
  if (monthIndex < 1) {
    year -= 1;
    monthIndex = count;
  } else if (monthIndex > count) {
    year += 1;
    monthIndex = 1;
  }
  return { eraId: cur.eraId, year, monthIndex };
}

/** Step the displayed year by ±1 on the era's own numbering, crossing the
 * anchor (and flipping the era, with no year zero) when needed. */
function stepYearCursor(calendar: Calendar, cur: CalendarCursor, delta: number): CalendarCursor {
  const era = eraById(calendar, cur.eraId);
  if (era === undefined) return cur;
  // Forward eras: +delta increases the display year. Backward eras count
  // down toward the anchor, so the user-facing "+" should move toward the
  // anchor — mirror the sign.
  const candidateYear = cur.year + (era.direction === 'forward' ? delta : -delta);
  const probe = toCanonicalDay(calendar, { era: cur.eraId, year: candidateYear, month: 1, day: 1 });
  if (probe.ok) {
    return { eraId: cur.eraId, year: candidateYear, monthIndex: cur.monthIndex };
  }
  // Past the boundary: advance the canonical day by one block-year in the
  // requested direction and re-derive the display date (1 BF → 1 IR is
  // crossed without a year zero this way).
  const at = toCanonicalDay(calendar, { era: cur.eraId, year: cur.year, month: 1, day: 1 });
  if (!at.ok) return cur;
  const yearLen = approxYearLength(calendar);
  const target = (at.value as number) + (era.direction === 'forward' ? delta : -delta) * yearLen;
  const back = toDisplayDate(calendar, target);
  if (!back.ok) return cur;
  return { eraId: back.value.era, year: back.value.year, monthIndex: cur.monthIndex };
}

/** Switch eras, keeping the same display year where possible. */
function setEraCursor(calendar: Calendar, cur: CalendarCursor, eraId: string): CalendarCursor {
  const probe = toCanonicalDay(calendar, { era: eraId, year: cur.year, month: 1, day: 1 });
  if (probe.ok) return { eraId, year: cur.year, monthIndex: cur.monthIndex };
  const era = eraById(calendar, eraId);
  if (era === undefined) return cur;
  return { eraId, year: era.year_one, monthIndex: cur.monthIndex };
}

/**
 * Jump to a typed display year within the current era (the editable year
 * input). `magnitude` is the absolute year the user typed; the sign follows
 * the current cursor so a backward era keeps counting its way. Invalid /
 * out-of-range years (no such day-1) leave the cursor unchanged.
 */
function setYearCursor(calendar: Calendar, cur: CalendarCursor, magnitude: number): CalendarCursor {
  const mag = Math.trunc(magnitude);
  if (!Number.isFinite(mag) || mag < 1) return cur;
  const signed = cur.year < 0 ? -mag : mag;
  const probe = toCanonicalDay(calendar, { era: cur.eraId, year: signed, month: 1, day: 1 });
  if (!probe.ok) return cur;
  return { eraId: cur.eraId, year: signed, monthIndex: cur.monthIndex };
}

// ---------- the widget ----------

export function calendarDatePicker(options: CalendarDatePickerOptions): Mountable {
  const {
    calendar,
    onNavigate,
    onOverlay,
    onSelect,
    onClear,
    onPeriod,
    locale = 'en',
    mode = 'popover',
    subDayPeriods = [],
    overlay = 'none',
    periodIndex = 0,
    showNow = true,
    showCanonicalDay = false,
    granularity = 'day',
    granularities,
    onGranularity,
    granularityClearLabel,
    onGranularityClear,
    testid,
  } = options;
  // The granularity levels this calendar actually exposes. `season` has no
  // cycle of its own yet (the schema models only months + optional weekdays —
  // see `calendarHasSeasons`), so it collapses to the month picker and is
  // dropped from the menu: "conditional to what the calendar definition exposes."
  const offeredGranularities = (granularities ?? []).filter(
    (g) => g !== 'season' || calendarHasSeasons(calendar),
  );
  // The segmented control shows when the calendar exposes >1 level, or when a
  // clear ("none") segment is offered alongside at least one level.
  const showGranularitySeg =
    offeredGranularities.length > 1 ||
    (offeredGranularities.length >= 1 && granularityClearLabel !== undefined);
  // `season` has no cycle of its own — pick at the month (the closest unit).
  const monthGranularity = granularity === 'month' || granularity === 'season';
  const messages: CalendarDatePickerMessages = {
    ...DEFAULT_CALENDAR_DATE_PICKER_MESSAGES,
    ...options.messages,
  };

  const todayDate: DisplayDate | null = canonicalToDisplay(calendar, options.today);
  const selectedDate: DisplayDate | null = canonicalToDisplay(calendar, options.value);

  // The view cursor: host-provided, else derived from the committed value,
  // else the calendar anchor. This is the single source of "what is shown".
  const cursor: CalendarCursor =
    options.cursor ?? deriveCursor(selectedDate ?? neutralDate(calendar));

  const cols = calendar.cycles.week?.labels.length ?? 7;

  const blocks = blocksFor(calendar, cursor.eraId, cursor.year, locale);
  const block = blocks[cursor.monthIndex - 1] ?? blocks[0];
  const era = eraById(calendar, cursor.eraId);

  function tid(suffix: string): Record<string, string> {
    return testid !== undefined ? { 'data-testid': `${testid}-${suffix}` } : {};
  }

  function fmtYearEra(): string {
    const affix = era !== undefined ? eraLabel(era, locale) : '';
    return `${cursor.year}${affix ? ` ${affix}` : ''}`;
  }

  // ---- navigation emitters (compute next cursor, hand to host) ----

  const emitStepBlock = (delta: number): void => {
    onNavigate?.(stepBlockCursor(calendar, cursor, delta));
    onOverlay?.('none');
  };
  const emitPickBlock = (monthIndex: number): void => {
    onNavigate?.({ eraId: cursor.eraId, year: cursor.year, monthIndex });
    onOverlay?.('none');
  };
  const emitStepYear = (delta: number): void => {
    onNavigate?.(stepYearCursor(calendar, cursor, delta));
  };
  const emitSetEra = (eraId: string): void => {
    onNavigate?.(setEraCursor(calendar, cursor, eraId));
  };
  const emitSetYear = (magnitude: number): void => {
    onNavigate?.(setYearCursor(calendar, cursor, magnitude));
  };
  // Coarse-granularity commits snap to the START of the chosen unit (the
  // stored value stays an exact canonical day; precision carries the fuzz).
  const commitMonth = (monthIndex: number): void => {
    const canonical = toCanonicalDay(calendar, {
      era: cursor.eraId,
      year: cursor.year,
      month: monthIndex,
      day: 1,
    });
    if (canonical.ok) onSelect?.(canonical.value);
  };
  // In year granularity the stepper commits directly (no separate "Select
  // year" button): each adjustment navigates the cursor AND commits day 1 of
  // that year. The dateField keeps the popover open for year-granularity
  // selects, so the user can step repeatedly and dismiss when done.
  const commitYearAt = (cur: CalendarCursor): void => {
    const canonical = toCanonicalDay(calendar, {
      era: cur.eraId,
      year: cur.year,
      month: 1,
      day: 1,
    });
    if (canonical.ok) onSelect?.(canonical.value);
  };
  const commitStepYear = (delta: number): void => {
    const next = stepYearCursor(calendar, cursor, delta);
    onNavigate?.(next);
    commitYearAt(next);
  };
  const commitSetYear = (magnitude: number): void => {
    const next = setYearCursor(calendar, cursor, magnitude);
    onNavigate?.(next);
    commitYearAt(next);
  };
  const commitSetEra = (eraId: string): void => {
    const next = setEraCursor(calendar, cursor, eraId);
    onNavigate?.(next);
    commitYearAt(next);
  };
  const emitToggleOverlay = (which: 'months' | 'era'): void => {
    onOverlay?.(overlay === which ? 'none' : which);
  };
  const commitDay = (day: number): void => {
    const date: DisplayDate = {
      era: cursor.eraId,
      year: cursor.year,
      month: cursor.monthIndex,
      day,
    };
    const canonical = toCanonicalDay(calendar, date);
    if (!canonical.ok) return;
    onSelect?.(canonical.value);
  };
  /**
   * Event wiring shared by every "commit this day" control (grid cell, list row,
   * festival day).
   *
   * These were the last controls in the popover still committing on `click`
   * alone. The granularity segments and the field trigger were moved to
   * `onPointerDown` for the #236 race: selecting a value remounts this subtree,
   * and a real gesture whose node is replaced between `mousedown` and `mouseup`
   * delivers its `click` to a node that is no longer the button — so the commit
   * is lost outright. Nothing makes the day cells immune to that; they were just
   * never converted. `commitDay` is idempotent, so a gesture that commits on
   * pointerdown and then also delivers its `click` re-commits the same canonical
   * day harmlessly.
   *
   * TOUCH deliberately keeps the click-only path. A touch `pointerdown` is also
   * the first event of a scroll drag, so committing there would turn a scroll of
   * the day grid into an accidental date selection, and `preventDefault()` on a
   * touch pointerdown cancels the scroll outright. Neither trade is worth making
   * on the surface where scrolling matters most — and the remount race is a
   * mouse-gesture problem to begin with.
   *
   * `preventDefault` (mouse only) keeps focus on the trigger: selecting a day
   * closes the popover, so letting focus land on the cell would strand it on
   * `<body>` once that cell is removed. `onClick` remains the keyboard path —
   * Enter/Space synthesise a click with no pointerdown at all.
   */
  const dayCommitHandlers = (
    day: number,
  ): { onPointerDown: (event: PointerEvent) => void; onClick: () => void } => ({
    onPointerDown: (event: PointerEvent): void => {
      if (event.button !== 0 || event.pointerType === 'touch') return;
      event.preventDefault();
      commitDay(day);
    },
    onClick: (): void => commitDay(day),
  });
  const goNow = (): void => {
    if (todayDate === null) return;
    onNavigate?.(deriveCursor(todayDate));
    onOverlay?.('none');
    const canonical = toCanonicalDay(calendar, todayDate);
    if (canonical.ok) onSelect?.(canonical.value);
  };

  // ---- selection / today predicates (per-day) ----

  const isSelectedDay = (month: number, day: number): boolean =>
    selectedDate !== null &&
    selectedDate.era === cursor.eraId &&
    selectedDate.year === cursor.year &&
    selectedDate.month === month &&
    selectedDate.day === day;

  const isTodayDay = (month: number, day: number): boolean =>
    todayDate !== null &&
    todayDate.era === cursor.eraId &&
    todayDate.year === cursor.year &&
    todayDate.month === month &&
    todayDate.day === day;

  // ---- declarative body (every node a real Mountable) ----

  const children: Mountable[] = [];
  // Optional switcher row, inside the card above the step-nav head.
  if (options.header !== undefined) {
    children.push(div({ class: 'dl-cdp-switch', ...tid('switch') }, [options.header]));
  }
  // Granularity segmented control (fine → coarse): switching it changes WHAT
  // the body lets you pick (day grid ↔ month picker ↔ year stepper) and is
  // emitted via `onGranularity` so the host can carry the precision.
  if (showGranularitySeg) children.push(viewGranularitySeg());

  if (granularity === 'year') {
    // Year precision — no month/day. The era control (year stepper + editable
    // input + era segments) IS the body, and it commits day 1 of the chosen
    // year on every change (no separate "Select year" button — the day/month
    // grids commit on click, so the year stepper does too).
    children.push(viewEraOverlay(true));
    children.push(viewFooter());
  } else if (monthGranularity) {
    // Month / season precision — pick a month (commits its 1st); the head
    // navigates years and (via the era overlay) eras. No day grid.
    children.push(viewCoarseHead());
    if (overlay === 'era') children.push(viewEraOverlay());
    children.push(viewMonthPick());
    children.push(viewFooter());
  } else {
    children.push(viewHead());

    if (overlay === 'era') children.push(viewEraOverlay());
    else if (overlay === 'months') children.push(viewMonthsOverlay());

    if (block === undefined) {
      // No blocks at all (degenerate calendar) — render only the head/footer.
    } else if (block.type === 'festival') {
      children.push(viewFestivalBand(block));
    } else if (calendar.cycles.week !== undefined) {
      children.push(viewWeekHeader());
      children.push(viewGrid(block));
    } else {
      children.push(viewDayList(block));
    }

    if (subDayPeriods.length > 0) children.push(viewTimeRow());
    children.push(viewFooter());
  }

  // A side-effect-only onMount: focus the roving grid cell once mounted (no
  // content is built here). It re-resolves the focused cell from the DOM.
  children.push(
    onMount((root) => {
      // Keep keyboard roving tabindex consistent on (re)mount: the selected
      // (or first) cell is the single tab stop. No focus steal on mount.
      const grid = root.querySelector('.dl-cdp-grid');
      if (grid === null) return;
      const cells = grid.querySelectorAll<HTMLElement>('.dl-cdp-cell:not(.is-blank)');
      cells.forEach((c, i) => c.setAttribute('tabindex', i === 0 ? '0' : '-1'));
    }),
  );

  function viewHead(): Mountable {
    return div({ class: 'dl-cdp-head' }, [
      button(
        {
          type: 'button',
          class: 'dl-cdp-nav',
          'aria-label': messages.prev,
          onClick: () => emitStepBlock(-1),
          ...tid('prev'),
        },
        [text('‹')],
      ),
      div({ class: 'dl-cdp-titlewrap' }, [
        button(
          {
            type: 'button',
            class: 'dl-cdp-pick',
            'aria-label': messages.month,
            'aria-haspopup': 'true',
            'aria-expanded': overlay === 'months' ? 'true' : 'false',
            onClick: () => emitToggleOverlay('months'),
            ...tid('month'),
          },
          [
            span({ class: `m${block?.type === 'festival' ? ' is-fest' : ''}` }, [
              text(block?.label ?? ''),
            ]),
            caret(),
          ],
        ),
        button(
          {
            type: 'button',
            class: 'dl-cdp-pick',
            'aria-label': messages.yearEra,
            'aria-haspopup': 'true',
            'aria-expanded': overlay === 'era' ? 'true' : 'false',
            onClick: () => emitToggleOverlay('era'),
            ...tid('year'),
          },
          [span({ class: 'y' }, [text(fmtYearEra())]), caret()],
        ),
      ]),
      button(
        {
          type: 'button',
          class: 'dl-cdp-nav',
          'aria-label': messages.next,
          onClick: () => emitStepBlock(1),
          ...tid('next'),
        },
        [text('›')],
      ),
    ]);
  }

  function caret(): Mountable {
    // The proper chevron-down icon (matches `dateField` and other disclosures),
    // not a text glyph.
    return span({ class: 'car', 'aria-hidden': 'true' }, [chevronDownIcon({ size: 14 })]);
  }

  function viewWeekHeader(): Mountable {
    const week = calendar.cycles.week!;
    // Abbreviate long weekday names so a wide localized name fits its column.
    const abbrevLen = week.labels.length >= 8 ? 4 : 3;
    return div(
      { class: 'dl-cdp-week', role: 'presentation' },
      week.labels.map((_, i) => {
        const full = localizedWeekday(calendar, i, locale);
        const short = full.length > abbrevLen + 1 ? full.slice(0, abbrevLen) : full;
        return div({ class: 'dl-cdp-wd', title: full }, [text(short)]);
      }),
    );
  }

  function viewGrid(b: GridBlock): Mountable {
    const days = daysInMonth(calendar, cursor.eraId, cursor.year, b.month);
    const firstWd =
      weekdayIndexOf(calendar, { era: cursor.eraId, year: cursor.year, month: b.month, day: 1 }) ?? 0;

    // The grid is a plain group of native <button> day cells (not an ARIA
    // `role="grid"` — that role mandates a `role="row"` layer our flat,
    // leading-blank-padded cell list doesn't have, which axe flags as
    // `aria-required-children`/`-parent`). Native button semantics +
    // `aria-pressed` + a descriptive `aria-label` carry the date for AT;
    // roving arrow-key navigation is wired on the container.
    const cells: Mountable[] = [];
    for (let i = 0; i < firstWd; i++) {
      cells.push(span({ class: 'dl-cdp-cell is-blank', 'aria-hidden': 'true' }));
    }
    for (let day = 1; day <= days; day++) {
      const sel = isSelectedDay(b.month, day);
      const today = isTodayDay(b.month, day);
      let cls = 'dl-cdp-cell';
      if (sel) cls += ' is-selected';
      if (today) cls += ' is-today';
      cells.push(
        button(
          {
            type: 'button',
            class: cls,
            'aria-pressed': sel ? 'true' : 'false',
            tabindex: '-1',
            'aria-label': cellAria(b, day),
            ...dayCommitHandlers(day),
          },
          [text(String(day))],
        ),
      );
    }
    return div(
      {
        class: 'dl-cdp-grid',
        role: 'group',
        'aria-label': `${b.label} ${fmtYearEra()}`,
        onKeyDown: (e: KeyboardEvent) => onGridKey(e, days),
        ...tid('grid'),
      },
      cells,
    );
  }

  function cellAria(b: GridBlock, day: number): string {
    const wdIdx = weekdayIndexOf(calendar, {
      era: cursor.eraId,
      year: cursor.year,
      month: b.month,
      day,
    });
    const wd = wdIdx !== null ? localizedWeekday(calendar, wdIdx, locale) : '';
    return `${day} ${b.label} ${fmtYearEra()}${wd ? `, ${wd}` : ''}`;
  }

  // Keyboard roving over the live grid DOM (a side-effect, not a rebuild).
  function onGridKey(e: KeyboardEvent, days: number): void {
    const gridEl = e.currentTarget as HTMLElement;
    const cells = gridEl.querySelectorAll<HTMLElement>('.dl-cdp-cell:not(.is-blank)');
    const current = gridEl.querySelector<HTMLElement>('.dl-cdp-cell[tabindex="0"]:not(.is-blank)');
    let idx = current !== null ? Array.prototype.indexOf.call(cells, current) : 0;
    if (idx < 0) idx = 0;
    let d = idx + 1; // 1-based day
    let move = true;
    switch (e.key) {
      case 'ArrowLeft':
        d = Math.max(1, d - 1);
        break;
      case 'ArrowRight':
        d = Math.min(days, d + 1);
        break;
      case 'ArrowUp':
        d = Math.max(1, d - cols);
        break;
      case 'ArrowDown':
        d = Math.min(days, d + cols);
        break;
      case 'Home':
        d = 1;
        break;
      case 'End':
        d = days;
        break;
      case 'PageUp':
        e.preventDefault();
        emitStepBlock(-1);
        return;
      case 'PageDown':
        e.preventDefault();
        emitStepBlock(1);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commitDay(d);
        return;
      default:
        move = false;
    }
    if (!move) return;
    e.preventDefault();
    const target = cells[d - 1];
    if (target === undefined) return;
    cells.forEach((c) => c.setAttribute('tabindex', '-1'));
    target.setAttribute('tabindex', '0');
    target.focus();
  }

  function viewDayList(b: GridBlock): Mountable {
    const days = daysInMonth(calendar, cursor.eraId, cursor.year, b.month);
    const rows: Mountable[] = [];
    for (let day = 1; day <= days; day++) {
      const sel = isSelectedDay(b.month, day);
      rows.push(
        button(
          {
            type: 'button',
            class: `dl-cdp-listrow${sel ? ' is-selected' : ''}`,
            'aria-pressed': sel ? 'true' : 'false',
            tabindex: '-1',
            ...dayCommitHandlers(day),
          },
          [
            span({ class: 'ln' }, [text(String(day))]),
            span({ class: 'lm' }, [
              text(day === 1 ? `${day} ${b.label}` : messages.dayN.replace('{day}', String(day))),
            ]),
          ],
        ),
      );
    }
    // Like the grid: a plain group of native buttons (not `role="listbox"` /
    // `role="option"`, which mandate a strict parent/child pairing axe
    // enforces). `aria-pressed` marks the committed day; roving ↑/↓ on the
    // container.
    return div(
      {
        class: 'dl-cdp-list',
        role: 'group',
        // The list scrolls (`overflow:auto`), so the container itself is a
        // keyboard-focusable scroll region (axe `scrollable-region-focusable`)
        // and the ↑/↓ roving entry point.
        tabindex: '0',
        'aria-label': `${b.label} ${fmtYearEra()}`,
        onKeyDown: (e: KeyboardEvent) => onListKey(e, days),
        ...tid('list'),
      },
      rows,
    );
  }

  function onListKey(e: KeyboardEvent, days: number): void {
    const listEl = e.currentTarget as HTMLElement;
    const rows = listEl.querySelectorAll<HTMLElement>('.dl-cdp-listrow');
    const current = listEl.querySelector<HTMLElement>('.dl-cdp-listrow[tabindex="0"]');
    let idx = current !== null ? Array.prototype.indexOf.call(rows, current) : 0;
    if (idx < 0) idx = 0;
    let d = idx + 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      d = Math.min(days, d + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      d = Math.max(1, d - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commitDay(d);
      return;
    } else {
      return;
    }
    const target = rows[d - 1];
    if (target === undefined) return;
    rows.forEach((r) => r.setAttribute('tabindex', '-1'));
    target.setAttribute('tabindex', '0');
    target.focus();
  }

  function viewFestivalBand(b: FestivalBlock): Mountable {
    const days = Math.max(1, b.days);
    const fdays: Mountable[] = [];
    for (let day = 1; day <= days; day++) {
      const sel = isSelectedDay(b.month, day);
      const inner: Mountable[] = [div({ class: 'fn' }, [text(b.label)])];
      if (b.days > 1) inner.push(div({ class: 'fs' }, [text(`${day} / ${b.days}`)]));
      fdays.push(
        button(
          {
            type: 'button',
            class: `dl-cdp-fday${sel ? ' is-selected' : ''}`,
            'aria-pressed': sel ? 'true' : 'false',
            'aria-label': `${b.label}${b.days > 1 ? ` ${day}` : ''} ${fmtYearEra()}`,
            ...dayCommitHandlers(day),
          },
          inner,
        ),
      );
    }
    return div({ class: 'dl-cdp-festwrap', ...tid('festival') }, [
      div({ class: 'dl-cdp-festlabel' }, [text(`✦ ${messages.intercalary}`)]),
      div({ class: 'dl-cdp-fest' }, fdays),
    ]);
  }

  function viewTimeRow(): Mountable {
    return div({ class: 'dl-cdp-time', ...tid('time') }, [
      div({ class: 'dl-cdp-time-label' }, [text(messages.time)]),
      div(
        { class: 'dl-cdp-watches' },
        subDayPeriods.map((p, i) => {
          const on = i === periodIndex;
          const inner: Mountable[] = [text(p.label ?? p.name)];
          if (p.time !== undefined) inner.push(span({ class: 'wt' }, [text(p.time)]));
          return button(
            {
              type: 'button',
              class: `dl-cdp-watch${on ? ' is-on' : ''}`,
              'aria-pressed': on ? 'true' : 'false',
              onClick: () => onPeriod?.(i),
            },
            inner,
          );
        }),
      ),
    ]);
  }

  function viewFooter(): Mountable {
    const valChildren: Mountable[] = [];
    if (selectedDate !== null) {
      // Read the value at the picker's granularity so Month/Year show the
      // partial date ("May 6 CE" / "6 CE"), not the full day.
      const formatted = formatDisplayDateAtPrecision(
        calendar,
        selectedDate,
        precisionForGranularity(granularity),
        { locale },
      );
      valChildren.push(div({ class: 'v', ...tid('value') }, [text(formatted)]));
      const canonical = toCanonicalDay(calendar, selectedDate);
      if (canonical.ok) {
        // The sub-line carries the active sub-day period (always, when present)
        // and — only when opted in — the engineering "canonical day N" detail.
        const parts: string[] = [];
        if (subDayPeriods.length > 0) {
          const p = subDayPeriods[periodIndex];
          if (p !== undefined) parts.push(p.label ?? p.name);
        }
        if (showCanonicalDay) parts.push(`${messages.canonicalDay} ${canonical.value as number}`);
        if (parts.length > 0) valChildren.push(div({ class: 's' }, [text(parts.join(' · '))]));
      }
    } else {
      valChildren.push(div({ class: 'empty', ...tid('empty') }, [text(messages.unset)]));
    }

    const foot: Mountable[] = [div({ class: 'dl-cdp-foot-val' }, valChildren)];
    if (todayDate !== null && showNow) {
      foot.push(
        button(
          { type: 'button', class: 'dl-cdp-btn ghost', onClick: goNow, ...tid('now') },
          [text(messages.now)],
        ),
      );
    }
    // Clear is an OPTIONAL affordance: render it only when the host wires
    // `onClear`. A picker for a value that is never "open-ended" (the session
    // / browse cursor) omits it, so no dead button appears.
    if (onClear !== undefined) {
      foot.push(
        button(
          { type: 'button', class: 'dl-cdp-btn ghost', onClick: () => onClear(), ...tid('clear') },
          [text(messages.clear)],
        ),
      );
    }
    return div({ class: 'dl-cdp-foot' }, foot);
  }

  // Coarse-granularity head: ‹ / › step the YEAR (not month blocks); the
  // centre control opens the era overlay to change year/era directly.
  function viewCoarseHead(): Mountable {
    return div({ class: 'dl-cdp-head' }, [
      button(
        {
          type: 'button',
          class: 'dl-cdp-nav',
          'aria-label': messages.prev,
          onClick: () => emitStepYear(-1),
          ...tid('prev'),
        },
        [text('‹')],
      ),
      div({ class: 'dl-cdp-titlewrap' }, [
        button(
          {
            type: 'button',
            class: 'dl-cdp-pick',
            'aria-label': messages.yearEra,
            'aria-haspopup': 'true',
            'aria-expanded': overlay === 'era' ? 'true' : 'false',
            onClick: () => emitToggleOverlay('era'),
            ...tid('year'),
          },
          [span({ class: 'y' }, [text(fmtYearEra())]), caret()],
        ),
      ]),
      button(
        {
          type: 'button',
          class: 'dl-cdp-nav',
          'aria-label': messages.next,
          onClick: () => emitStepYear(1),
          ...tid('next'),
        },
        [text('›')],
      ),
    ]);
  }

  // Month-granularity body: a month grid whose cells COMMIT (the 1st of the
  // month) rather than just navigate. Mirrors `viewMonthsOverlay`'s chrome.
  function viewMonthPick(): Mountable {
    // `--inflow` keeps the month grid in normal flow BELOW the year head (the
    // base `.dl-cdp-months` is an absolute overlay used by the day-view month
    // popover, which would otherwise float over and hide the year control).
    return div(
      {
        class: 'dl-cdp-months dl-cdp-months--inflow',
        role: 'group',
        'aria-label': messages.month,
        ...tid('month-pick'),
      },
      blocks.map((b) => {
        const on =
          selectedDate !== null &&
          selectedDate.era === cursor.eraId &&
          selectedDate.year === cursor.year &&
          selectedDate.month === b.month;
        const isFest = b.type === 'festival';
        return button(
          {
            type: 'button',
            class: `dl-cdp-mo${isFest ? ' is-fest' : ''}${on ? ' is-sel' : ''}`,
            'aria-pressed': on ? 'true' : 'false',
            onClick: () => commitMonth(b.month),
          },
          [
            span({ class: 'mi', 'aria-hidden': 'true' }, [text(isFest ? '·' : String(b.month))]),
            text(b.label),
          ],
        );
      }),
    );
  }

  function viewMonthsOverlay(): Mountable {
    return div(
      { class: 'dl-cdp-months', role: 'group', 'aria-label': messages.month, ...tid('months') },
      blocks.map((b) => {
        const on = b.month === cursor.monthIndex;
        const isFest = b.type === 'festival';
        return button(
          {
            type: 'button',
            class: `dl-cdp-mo${isFest ? ' is-fest' : ''}${on ? ' is-sel' : ''}`,
            'aria-pressed': on ? 'true' : 'false',
            onClick: () => emitPickBlock(b.month),
          },
          [
            span({ class: 'mi', 'aria-hidden': 'true' }, [text(isFest ? '·' : String(b.month))]),
            text(b.label),
          ],
        );
      }),
    );
  }

  // `commit=true` (year granularity, where the stepper IS the body) commits the
  // year on every change; `false` (the day/month toggled overlay) only
  // navigates the displayed year.
  function viewEraOverlay(commit = false): Mountable {
    const stepYear = commit ? commitStepYear : emitStepYear;
    const setYear = commit ? commitSetYear : emitSetYear;
    const setEra = commit ? commitSetEra : emitSetEra;
    const row: Mountable[] = [
      div({ class: 'dl-cdp-era-step' }, [
        button(
          {
            type: 'button',
            'aria-label': `${messages.yearEra} −`,
            onClick: () => stepYear(-1),
            ...tid('year-dec'),
          },
          [text('−')],
        ),
        input({
          type: 'number',
          min: '1',
          inputmode: 'numeric',
          class: 'val',
          value: String(Math.abs(cursor.year)),
          'aria-label': messages.yearEra,
          ...tid('year-input'),
          // Commit on blur / Enter (not per-keystroke) so typing a multi-digit
          // year doesn't re-navigate mid-entry. Empty / invalid → no-op.
          onChange: (event: Event) => {
            const raw = (event.target as HTMLInputElement).value;
            if (raw.trim() !== '') setYear(Number(raw));
          },
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              const raw = (event.target as HTMLInputElement).value;
              if (raw.trim() !== '') setYear(Number(raw));
            }
          },
        }),
        button(
          {
            type: 'button',
            'aria-label': `${messages.yearEra} +`,
            onClick: () => stepYear(1),
            ...tid('year-inc'),
          },
          [text('+')],
        ),
      ]),
    ];

    if (calendar.eras.length > 1) {
      row.push(
        div(
          { class: 'dl-cdp-seg', role: 'group', 'aria-label': messages.yearEra },
          calendar.eras.map((e) => {
            const on = e.id === cursor.eraId;
            return button(
              {
                type: 'button',
                class: `dl-cdp-seg-item${on ? ' is-on' : ''}`,
                'aria-pressed': on ? 'true' : 'false',
                onClick: () => setEra(e.id),
              },
              [text(eraLabel(e, locale))],
            );
          }),
        ),
      );
    }

    const wrap: Mountable[] = [div({ class: 'dl-cdp-era-row' }, row)];
    if (era?.direction === 'backward' && messages.backwardEraHint.length > 0) {
      wrap.push(
        div({ class: 'dl-cdp-era-hint' }, [
          text(messages.backwardEraHint.replace('{era}', eraLabel(era, locale))),
        ]),
      );
    }
    return div({ class: 'dl-cdp-era', ...tid('era') }, wrap);
  }

  // Full-width segmented control. An optional leading "none" segment (clears
  // the value) precedes the calendar's exposed granularity levels, in the
  // canonical fine → coarse order. The active level is `aria-pressed` so the
  // selection reads to AT by state, not colour alone.
  function viewGranularitySeg(): Mountable {
    const order: readonly CalendarGranularity[] = ['day', 'month', 'season', 'year'];
    const offered = order.filter((g) => offeredGranularities.includes(g));
    // When there is no committed value the bound is "none" (open-ended), so the
    // clear segment is the active one and no granularity level is.
    const noneActive = selectedDate === null;
    // A `season`-precision bound collapses to the month picker when the calendar
    // has no season cycle (the season segment isn't offered), so Month is the
    // active level for it — otherwise no segment would read as selected.
    const activeGranularity: CalendarGranularity =
      granularity === 'season' && !offered.includes('season') ? 'month' : granularity;
    const segs: Mountable[] = [];
    if (granularityClearLabel !== undefined) {
      segs.push(
        button(
          {
            type: 'button',
            class: `dl-cdp-seg-item dl-cdp-gran-item dl-cdp-gran-clear${noneActive ? ' is-on' : ''}`,
            'aria-pressed': noneActive ? 'true' : 'false',
            // Commit on POINTER-DOWN, not click: switching granularity changes
            // the bound's precision, which remounts this picker's subtree. A
            // real click's mousedown→(remount)→click would land the click on a
            // replaced node and be lost. Firing on pointerdown commits the
            // (idempotent) precision switch within the same gesture, before the
            // remount. preventDefault keeps focus on the trigger. The onClick
            // keeps keyboard activation (Enter/Space → click, no pointerdown).
            // (#236 — the validity-granularity click race.)
            onPointerDown: (event: PointerEvent) => {
              if (event.button !== 0) return;
              event.preventDefault();
              onGranularityClear?.();
            },
            onClick: () => onGranularityClear?.(),
            ...tid('gran-none'),
          },
          [text(granularityClearLabel)],
        ),
      );
    }
    for (const g of offered) {
      const on = !noneActive && g === activeGranularity;
      segs.push(
        button(
          {
            type: 'button',
            class: `dl-cdp-seg-item dl-cdp-gran-item${on ? ' is-on' : ''}`,
            'aria-pressed': on ? 'true' : 'false',
            // Commit on pointerdown (see gran-none above) so the click isn't
            // lost to the precision-switch remount; idempotent, so the trailing
            // click re-applying the same level is harmless.
            onPointerDown: (event: PointerEvent) => {
              if (event.button !== 0) return;
              event.preventDefault();
              onGranularity?.(g);
            },
            onClick: () => onGranularity?.(g),
            ...tid(`gran-${g}`),
          },
          [text(messages.granularityOptions[g])],
        ),
      );
    }
    return div(
      {
        class: 'dl-cdp-gran dl-cdp-seg',
        role: 'group',
        'aria-label': messages.granularity,
        ...tid('granularity'),
      },
      segs,
    );
  }

  return div(
    {
      class: `dl-cdp${mode === 'inline' ? ' dl-cdp--inline' : ''}`,
      style: `--cdp-cols:${cols}`,
      role: 'group',
      'aria-label': messages.grid,
      ...(testid !== undefined ? { 'data-testid': testid } : {}),
    },
    children,
  );
}

/** Convert a committed canonical-day axis value to a display date, or null. */
function canonicalToDisplay(
  calendar: Calendar,
  v: CanonicalDay | number | null | undefined,
): DisplayDate | null {
  if (v === null || v === undefined) return null;
  const r = toDisplayDate(calendar, v);
  return r.ok
    ? { era: r.value.era, year: r.value.year, month: r.value.month, day: r.value.day }
    : null;
}

/** The view cursor that frames a display date (era/year/month). */
function deriveCursor(date: DisplayDate): CalendarCursor {
  return { eraId: date.era, year: date.year, monthIndex: date.month };
}

/**
 * The starting view cursor for a committed `value` — the exact derivation the
 * picker uses internally when `cursor` is omitted (the selected day's
 * era/year/month, or the calendar anchor when nothing is selected). A host
 * that OWNS the cursor (so it can persist navigation) calls this once to seed
 * the slice when it first opens the grid for a value.
 */
export function seedCursor(
  calendar: Calendar,
  value: CanonicalDay | number | null | undefined,
): CalendarCursor {
  return deriveCursor(canonicalToDisplay(calendar, value) ?? neutralDate(calendar));
}
